package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.model.EventContent
import fr.manyverse.app.domain.model.EventType
import fr.manyverse.app.domain.model.GameEvent
import fr.manyverse.app.domain.repository.GameEventCacheRepository
import fr.manyverse.app.domain.repository.GameEventRepository
import kotlin.time.Instant
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch

private const val TAG = "CachingGameEventRepository"

/**
 * Decorator over [GameEventRepository] that serves the most recent unfiltered events
 * from an in-memory cache on cold start, while triggering a background refresh.
 *
 * Filtered queries (eventTypes / since / non-zero offset) bypass the cache — only the
 * canonical "last N events of a table" view is cached.
 */
class CachingGameEventRepository(
    private val remoteRepository: GameEventRepository,
    private val cacheRepository: GameEventCacheRepository,
    private val backgroundScope: CoroutineScope
) : GameEventRepository {

    override suspend fun createEvent(
        tableId: String,
        type: EventType,
        triggeredBy: String,
        targetParticipants: List<String>,
        content: EventContent
    ): Result<GameEvent> = remoteRepository.createEvent(
        tableId = tableId,
        type = type,
        triggeredBy = triggeredBy,
        targetParticipants = targetParticipants,
        content = content
    ).also { result ->
        result.onSuccess { event -> cacheRepository.upsertEvent(tableId, event) }
    }

    override suspend fun getTableEvents(
        tableId: String,
        limit: Int,
        offset: Int,
        eventTypes: List<EventType>,
        since: Instant?
    ): Result<List<GameEvent>> {
        val canCache = offset == 0 && eventTypes.isEmpty() && since == null
        if (!canCache) return remoteRepository.getTableEvents(tableId, limit, offset, eventTypes, since)

        val cached = cacheRepository.getEvents(tableId)
        if (cached != null) {
            refreshInBackground(tableId, limit)
            return Result.success(cached.take(limit))
        }

        return remoteRepository.getTableEvents(tableId, limit, offset, eventTypes, since)
            .also { result ->
                result.onSuccess { events -> cacheRepository.setEvents(tableId, events) }
            }
    }

    override fun observeNewEvents(tableId: String): Flow<GameEvent> =
        remoteRepository.observeNewEvents(tableId)
            .onEach { event -> cacheRepository.upsertEvent(tableId, event) }

    private fun refreshInBackground(tableId: String, limit: Int) {
        backgroundScope.launch {
            remoteRepository.getTableEvents(tableId, limit, 0, emptyList(), null)
                .onSuccess { events -> cacheRepository.setEvents(tableId, events) }
                .onFailure {
                    AppLogger.warn(TAG, "Background cache refresh failed for table=$tableId")
                }
        }
    }
}
